import React, { Children, useMemo, useEffect, useState, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import Portal from '@reach/portal'
import { QueryClientProvider } from '@tanstack/react-query'
import { lakshmi } from '@machobear/design-system/themes/lakshmi'
import { ThemeProvider, ToastProvider } from '@machobear/design-system'
import { themeProviderOptions } from '@/lib/themes'
import { getStore } from '@/store'
import { CssUnit } from '@/types'
import queryClient from '@/components/query-client'
import { ModalizeProvider } from './context'
import { Modal, ModalProps, ModalRefs } from './modal'

type InternalModalProps = ModalProps & ModalRefs
type AllSelectedModalProps = Omit<InternalModalProps, 'children'>
type SelectedModalProps = Omit<ModalProps, 'children'>

type ModalProviderProps = {
  dispose: () => void
  children: React.ReactNode
  modalProps: AllSelectedModalProps
}

type PortalProps = {
  children?: React.ReactNode
}

export type AdditionalModalProps = {
  ModalTitle: React.ComponentType<PortalProps>
  ModalFooter: React.ComponentType<PortalProps>
}

const ModalWithProvider: React.FC<ModalProviderProps> = ({ dispose, children, modalProps }) => {
  const [width, setWidth] = useState<CssUnit>()

  const { width: staticWidth, ...modalPropsRest } = modalProps

  return (
    <ModalizeProvider value={{ dispose, setWidth }}>
      <Modal
        {...modalPropsRest}
        width={width ?? staticWidth}
        closeConfig={modalProps.closeConfig ? modalProps.closeConfig : undefined}
      >
        {children}
      </Modal>
    </ModalizeProvider>
  )
}

type Base = {}

const ModalInjector = <T extends Base>({
  dispose,
  modalProps,
  props,
  Component
}: Omit<ModalProviderProps, 'children' | 'modalProps'> & {
  modalProps: SelectedModalProps
  props: NoRefs<T>
  Component: React.ComponentType<T>
}) => {
  const [titleRef, setTitle] = useState<HTMLDivElement | null>(null)
  const [footerRef, setFooter] = useState<HTMLDivElement | null>(null)
  const [hasFooter, setHasFooter] = useState(false)
  const footer = useMemo(() => ({ current: footerRef }), [footerRef])
  const title = useMemo(() => ({ current: titleRef }), [titleRef])
  const ModalTitle = useMemo(() => {
    const Title: React.FC<PortalProps> = ({ children }) =>
      title?.current ? (
        <Portal containerRef={title} type="div">
          {children}
        </Portal>
      ) : null
    return Title
  }, [title])
  const ModalFooter = useMemo(() => {
    const Footer: React.FC<PortalProps> = ({ children }) => {
      useEffect(() => {
        const count = Children.count(children)
        setHasFooter(count > 0)
        return () => {
          setHasFooter(false)
        }
      }, [children])
      return footer?.current ? (
        <Portal containerRef={footer} type="div">
          {children}
        </Portal>
      ) : null
    }
    return Footer
  }, [footer, setHasFooter])
  return (
    <ModalWithProvider
      dispose={dispose}
      modalProps={{
        ...modalProps,
        titleRef: setTitle,
        footerRef: setFooter,
        hasFooter,
        ...props
      }}
    >
      <Component
        {...(props as T)}
        // onClose={modalProps.onClose}
        ModalTitle={ModalTitle}
        ModalFooter={ModalFooter}
      />
    </ModalWithProvider>
  )
}

type Options = SelectedModalProps & {
  unmountDelay?: number
  mountingNode?: HTMLElement
  id?: string
}

type ModalOptions = {
  overrideExisting?: boolean
} & SelectedModalProps

type DisposeFunc = () => void
type ShowFunc<T> = (props: T, options?: ModalOptions) => DisposeFunc
type NoRefs<T> = Omit<T, 'ModalTitle' | 'ModalFooter'>

const createModal = <T extends Base>(
  Component: React.ComponentType<T>,
  options?: Options
): ShowFunc<NoRefs<T>> => {
  return (
    props: NoRefs<T>,
    { overrideExisting = true, ...modalOptions }: ModalOptions = {}
  ): DisposeFunc => {
    const { id, unmountDelay = 100, mountingNode, ...modalProps } = options ?? {}
    const previous = id && document.getElementById(id)
    if (previous) {
      const previousRoot = createRoot(previous)
      try {
        const parent = previous.parentNode
        parent?.removeChild(previous)

        //put at bottom of dom to draw over everything
        if (!overrideExisting) {
          parent?.appendChild(previous)
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          return () => {}
        }

        previousRoot.unmount()
      } catch (err) {
        // ignore
      }
    }
    const theNode = mountingNode || document.body
    const wrapper = theNode.appendChild(document.createElement('div'))
    const root = createRoot(wrapper)
    id && wrapper.setAttribute('id', id)
    wrapper.setAttribute('aria-modal', 'true')
    const store = getStore()

    const observer = new MutationObserver(() => {
      if (wrapper.getAttribute('aria-hidden')) {
        wrapper.removeAttribute('aria-hidden')
        observer.disconnect()
      }
    })

    observer.observe(wrapper, {
      attributeFilter: ['aria-hidden']
    })

    try {
      root.render(
        <React.StrictMode>
          <Suspense fallback={null}>
            <Provider store={store}>
              <QueryClientProvider client={queryClient}>
                <ThemeProvider theme={lakshmi} options={themeProviderOptions}>
                  <ToastProvider>
                    <ModalInjector
                      dispose={dispose}
                      modalProps={{ ...modalProps, ...modalOptions }}
                      Component={Component}
                      props={props}
                    />
                  </ToastProvider>
                </ThemeProvider>
              </QueryClientProvider>
            </Provider>
          </Suspense>
        </React.StrictMode>
      )
    } catch (e) {
      console.error(e)
      throw e
    }

    function dispose() {
      return new Promise<void>((resolve, reject) => {
        setTimeout(() => {
          try {
            root.unmount()
            theNode.removeChild(wrapper)
            resolve()
          } catch (err) {
            // ignore
            reject(err)
          }
        }, unmountDelay)
      })
    }

    return dispose
  }
}

export default createModal
